import api from './api';
import { API_ENDPOINTS } from './config';

/**
 * Equipment Service
 * Handles all equipment-related API calls including CRUD operations, assignments, and location-based queries
 */

/**
 * Fetch all equipment
 * @returns {Promise<Object>} { success, data: [equipment items], error }
 */
export const getAllEquipment = async (limit = 50, page = 1) => {
  try {
    // Construct the query parameters for limit and page
    const response = await api.get(API_ENDPOINTS.EQUIPMENT.GET_ALL, {
      params: { limit, page }, // Include limit and page in the query
      headers: {
      'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      "Content-Type": "application/json",
      "Accept": "application/json",
    },
    });
    return response;
  } catch (error) {
    console.error('Get All Equipment Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to fetch equipment',
    };
  }
};


export const getAllEquipmentRequest = async () => {
  try {
    const response = await api.get(API_ENDPOINTS.EQUIPMENT.GET_EQUIPMENT_REQUEST, {
    });
    return response;
  } catch (error) {
    console.error('Get All Equipment Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to fetch equipment',
    };
  }
};

export const acceptrejectEquipment = async (payload) => {
  try {
    const response = await api.post(API_ENDPOINTS.EQUIPMENT.ACCEPT_REJECT_REQUEST, payload, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response;
  } catch (error) {
    console.error('Create Equipment Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to create equipment',
    };
  }
};

/**
 * Fetch equipment by ID
 * @param {number|string} equipmentId - The equipment ID
 * @returns {Promise<Object>} { success, data: equipment object, error }
 */
export const getEquipmentById = async (equipmentId) => {
  try {
    const response = await api.get(`${API_ENDPOINTS.EQUIPMENT.GET_BY_ID}/${equipmentId}`);
    return response;
  } catch (error) {
    console.error('Get Equipment By ID Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to fetch equipment details',
    };
  }
};

/**
 * Create new equipment
 * @param {FormData} formData - Form data including equipment details and optional file upload
 * @returns {Promise<Object>} { success, data: created equipment, error }
 */
export const createEquipment = async (formData) => {
  try {
    const response = await api.post(API_ENDPOINTS.EQUIPMENT.CREATE, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  } catch (error) {
    console.error('Create Equipment Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to create equipment',
    };
  }
};

/**
 * Update existing equipment
 * @param {number|string} equipmentId - The equipment ID
 * @param {FormData|Object} data - Equipment update data (FormData for file uploads, Object otherwise)
 * @returns {Promise<Object>} { success, data: updated equipment, error }
 */
export const updateEquipment = async (equipmentId, data) => {
  try {
    const isFormData = data instanceof FormData;
    const config = isFormData
      ? {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      : {};

    const response = await api.put(`${API_ENDPOINTS.EQUIPMENT.UPDATE}/${equipmentId}`, data, config);
    return response;
  } catch (error) {
    console.error('Update Equipment Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to update equipment',
    };
  }
};

/**
 * Delete equipment
 * @param {number|string} equipmentId - The equipment ID
 * @returns {Promise<Object>} { success, data, error }
 */
export const deleteEquipment = async (equipmentId) => {
  try {
    const response = await api.delete(`${API_ENDPOINTS.EQUIPMENT.DELETE}/${equipmentId}`);
    return response;
  } catch (error) {
    console.error('Delete Equipment Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to delete equipment',
    };
  }
};

/**
 * Assign equipment to crew/project
 * @param {Object} assignmentData - Assignment details { equipment_id, crew_id?, project_id?, assigned_to?, assignment_date?, return_date? }
 * @returns {Promise<Object>} { success, data: assignment details, error }
 */
export const assignEquipment = async (assignmentData) => {
  try {
    const response = await api.post(API_ENDPOINTS.EQUIPMENT.ASSIGN, assignmentData);
    return response;
  } catch (error) {
    console.error('Assign Equipment Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to assign equipment',
    };
  }
};

/**
 * Return equipment from assignment
 * @param {Object} returnData - Return details { equipment_id, return_date?, notes? }
 * @returns {Promise<Object>} { success, data: return details, error }
 */
export const returnEquipment = async (returnData) => {
  try {
    const response = await api.post(API_ENDPOINTS.EQUIPMENT.RETURN, returnData);
    return response;
  } catch (error) {
    console.error('Return Equipment Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to return equipment',
    };
  }
};

/**
 * Fetch equipment grouped by location
 * @returns {Promise<Object>} { success, data: equipment grouped by location, error }
 */
export const getEquipmentByLocation = async () => {
  try {
    const response = await api.get(API_ENDPOINTS.EQUIPMENT.BY_LOCATION);
    return response;
  } catch (error) {
    console.error('Get Equipment By Location Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to fetch equipment by location',
    };
  }
};

/**
 * Fetch all equipment
 * @returns {Promise<Object>} { success, data: [equipment items], error }
 */
export const getCrew = async (searchText = "") => {
  try {
    const response = await api.get(API_ENDPOINTS.CREW.GET_NAMES, {
      params: {
        query: searchText,  // 🔥 attaches ?query=value
      },
    });

    return response;
  } catch (error) {
    console.error("Get All Crew Error:", error);
    return {
      success: false,
      data: null,
      error: "Failed to fetch Crew",
    };
  }
};


// Add these to your equipmentService.js

/**
 * Fetch all equipment categories for dropdowns
 */
export const getEquipmentCategories = async () => {
  try {
    const response = await api.get(API_ENDPOINTS.EQUIPMENT.CATEGORIES || '/admin/equipment-categories');
    return response.data; // Adjusted to return .data based on standard response shapes
  } catch (error) {
    console.error('Get Categories Error:', error);
    return {
      success: false,
      data: [],
      error: 'Failed to fetch categories',
    };
  }
};

/**
 * Step 1: Upload CSV and get JSON preview
 * @param {FormData} formData - Contains the 'file' key with the CSV
 */
export const previewEquipmentCsv = async (formData) => {
  try {
    const response = await api.post(
      API_ENDPOINTS.EQUIPMENT.BULK_PREVIEW || '/admin/bulk-upload-equipment',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    // Most api wrappers return the body already.
    // If response.data exists, use it. Otherwise, use the response itself.
    return response.data !== undefined ? response.data : response;
  } catch (error) {
    console.error('CSV Preview Error:', error);
    // Return a consistent error structure
    return {
      error: true,
      message: error.response?.data?.message || 'Failed to parse CSV file'
    };
  }
};

/**
 * Step 2: Save the mapped equipment data to the database
 * @param {Object} data - Format: { equipment_list: [...] }
 */
export const bulkSaveEquipment = async (data) => {
  try {
    const response = await api.post(
      API_ENDPOINTS.EQUIPMENT.BULK_SAVE || '/admin/bulk-upload-save-equipment',
      data
    );
    return response.data;
  } catch (error) {
    console.error('Bulk Save Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to save equipment data',
    };
  }
};

/**
 * Publish equipment (Remove from draft status)
 * @param {number|string} equipmentId - The equipment ID to publish
 * @returns {Promise<Object>} { success, data, error }
 */
export const publishEquipment = async (equipmentId) => {
  try {
    // Assuming you follow the patch pattern for status updates
    const response = await api.put(`${API_ENDPOINTS.EQUIPMENT.PUBLISH}/${equipmentId}`, {}, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return response;
  } catch (error) {
    console.error('Publish Equipment Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to publish equipment',
    };
  }
};

export const updateEquipmentQuantity = async (payload) => {
  try {
    const response = await api.post(API_ENDPOINTS.EQUIPMENT.QUANTITY, payload, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response;
  } catch (error) {
    console.error('Create Equipment Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to create equipment',
    };
  }
};
